import * as stylex from '@stylexjs/stylex';
import clsx from 'clsx';
import _ from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EditableText } from '@/core/EditableText.atom';
import SelectItem from '@/core/SelectItem.organism';
import { AddFormulaParameterModal } from '@/formula/AddFormulaParameterModal.molecule';
import { formulaStyles as styles } from '@/formula/formula.styles';
import { formulaIdentifierCollides } from '@/utilities/formulaVariables.utilities';
import { isItemRedacted } from '@/utilities/redaction.utilities';
import { errorToast, warnToast } from '@/utilities/toast.utilities';
import { faExclamationTriangle, faPencil, faPlus, faRemove } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { Icon } from '@seeqdev/qomponents';
import type { ComponentProps, ReactNode } from 'react';

const ITEM_SELECTOR_WIDTH = 200;
const DEFAULT_NAME_MAX_DISPLAY_CHARS = 500;

export interface FormulaEditorParam {
  name: string;
  item: { id: string; name: string; type?: string };
  identifier?: string;
  listParameters?: FormulaEditorParam[];
  inputFeatureId?: string; // used for mapping parameters to inputs when editing an existing formula for a model derived feature
  /** Declared item type used to validate the formula when no real item is mapped. */
  outputItemType?: string;
  outputItemMetadata?: Record<string, string>;
}

interface FormulaParametersTableProps {
  resizeEnabled?: boolean;
  parameters: FormulaEditorParam[];
  updateParameterCallback: (updated: FormulaEditorParam, originalParam: FormulaEditorParam) => void;
  removeParameterCallback: (identifier: string) => void;
  insertParameter?: (param: string) => void;
  additionalItems?: ComponentProps<typeof SelectItem>['additionalItems'];
  excludeStoreItems?: boolean;
  onItemSelect: ComponentProps<typeof SelectItem>['onSelect'];
  includeAddToDisplayPane?: boolean;
  suppressAddOrRemoveOrChangeParameter?: boolean;
  listParametersEditable?: boolean;
  convertStringToParametersList?: (listParametersString: string) => FormulaEditorParam[];
  latestParameterListAdded?: string;
  setLatestParameterListAdded?: (identifier: string | undefined) => void;
  outputItemId?: string;
  readOnly?: boolean;
  itemSelectorWidth?: number;
  /** Caps the displayed length of each parameter identifier */
  nameMaxDisplayChars?: number;
  /**
   * Custom renderer for the item cell. Also uses the "Item Type" header and hides
   * the edit-parameter action.
   */
  renderItemCell?: (parameter: FormulaEditorParam, idx: number) => ReactNode;
}

export const FormulaParametersTable = ({
  resizeEnabled = true,
  additionalItems,
  insertParameter,
  parameters,
  onItemSelect,
  excludeStoreItems,
  updateParameterCallback,
  removeParameterCallback,
  includeAddToDisplayPane = true,
  suppressAddOrRemoveOrChangeParameter = false,
  listParametersEditable = false,
  convertStringToParametersList,
  latestParameterListAdded,
  setLatestParameterListAdded,
  outputItemId,
  readOnly = false,
  itemSelectorWidth = ITEM_SELECTOR_WIDTH,
  nameMaxDisplayChars = DEFAULT_NAME_MAX_DISPLAY_CHARS,
  renderItemCell,
}: FormulaParametersTableProps) => {
  const { t } = useTranslation();
  const [formulaParameterModalItem, setFormulaParameterModalItem] = useState<FormulaEditorParam | undefined>(undefined);

  const actionStyle = resizeEnabled ? styles.parameterAction : styles.compactParameterAction;

  const updateParameter = (updatedParameter: FormulaEditorParam, originalParameter: FormulaEditorParam) => {
    if (
      updatedParameter.name !== originalParameter.name &&
      formulaIdentifierCollides(
        _.map(parameters, (parameter) => parameter.identifier ?? ''),
        updatedParameter.identifier ?? '',
        originalParameter.identifier,
      )
    ) {
      warnToast({ messageKey: 'FORMULA.VARIABLE_UNIQUE' });
    } else {
      updateParameterCallback(updatedParameter, originalParameter);
    }
  };

  const updateParameterList = (parameter: FormulaEditorParam, parameterList: string) => {
    if (!parameterList.match(/^(\$?\w+)(,\s*\$?\w+)*$/g)) {
      errorToast({
        messageKey: 'FORMULA.LIST_PARAMETER_INVALID',
        messageParams: { identifier: `$${parameter.identifier}`, variableListString: parameterList },
      });
      return;
    }
    const listParameters = convertStringToParametersList ? convertStringToParametersList(parameterList) : undefined;

    updateParameter(
      {
        ...parameter,
        item: { id: parameter.item.id, name: listParameters?.map((param) => `$${param.identifier}`).join(', ') ?? '' },
        listParameters,
      },
      parameter,
    );
  };

  const clickRef = React.useCallback(
    (node: HTMLParagraphElement) => {
      if (node) {
        node.focus();
        node.click();
        if (setLatestParameterListAdded) {
          setLatestParameterListAdded(undefined);
        }
      }
    },
    [setLatestParameterListAdded],
  );

  return (
    <>
      <table {...stylex.props(styles.parameterTable)}>
        <thead>
          <tr>
            <th {...stylex.props(styles.parameterNameHeading)}>{t('NAME')}</th>
            <th {...stylex.props(styles.parameterItemHeading)}>{renderItemCell ? t('ITEM_TYPE') : t('ITEM')}</th>
            <th {...stylex.props(styles.parameterActionsHeading)} />
          </tr>
        </thead>
        <tbody>
          {_.isEmpty(parameters) && (
            <tr {...stylex.props(styles.parameterRow, styles.emptyResults)}>
              <td colSpan={3} {...stylex.props(styles.emptyParameterCell)}>
                {t('FORMULA.NO_VARIABLES')}
              </td>
            </tr>
          )}
          {_.map(parameters, (parameter, idx) => (
            <tr
              key={parameter.identifier}
              {...stylex.props(styles.parameterRow)}
              data-testid={`formulaParameter_${idx}`}>
              <td {...stylex.props(styles.parameterIdentifierCell)}>
                <EditableText
                  testId="rowName"
                  value={parameter.identifier as string}
                  inputXstyle={[styles.parameterIdentifierInput, styles.parameterIdentifier]}
                  textXstyle={[styles.parameterText, styles.parameterIdentifier]}
                  textPrefix="$"
                  maxDisplayChars={nameMaxDisplayChars}
                  onUpdate={(identifier) => updateParameter({ ...parameter, identifier, name: identifier }, parameter)}
                />
              </td>
              <td
                {...stylex.props(
                  styles.parameterCell,
                  resizeEnabled ? styles.parameterItemWidth(itemSelectorWidth) : styles.parameterItemContent,
                )}>
                {renderItemCell ? (
                  renderItemCell(parameter, idx)
                ) : resizeEnabled && !suppressAddOrRemoveOrChangeParameter ? (
                  <SelectItem
                    testId={`select_${idx}`}
                    selectedItemId={parameter.item.id}
                    excludeStoreItems={excludeStoreItems}
                    showAddToDisplayPane={includeAddToDisplayPane}
                    additionalItems={additionalItems}
                    paramIndex={`${idx}`}
                    identifier={parameter.identifier}
                    extraClassNames={`select_${parameter.name}`}
                    onSelect={onItemSelect}
                    selectByIdAndGroup={true}
                    showLane={true}
                    outputItemId={outputItemId}
                  />
                ) : (
                  <span>
                    {isItemRedacted(parameter.item) && (
                      <Icon
                        tooltip={t('NO_ITEM_ACCESS')}
                        icon={faExclamationTriangle}
                        xstyle={styles.redactedParameter}
                      />
                    )}
                    {parameter.listParameters !== undefined && listParametersEditable ? (
                      <EditableText
                        testId="parameterList"
                        value={parameter.item.name}
                        inputXstyle={styles.parameterListInput}
                        textXstyle={styles.parameterText}
                        maxDisplayChars={500}
                        onUpdate={(parameterList) => updateParameterList(parameter, parameterList)}
                        placeholder={t('FORMULA.LIST_PARAMETER_PLACEHOLDER')}
                        clickRef={latestParameterListAdded === parameter.identifier ? clickRef : undefined}
                      />
                    ) : (
                      parameter.item.name
                    )}
                  </span>
                )}
              </td>
              <td
                className={clsx(
                  'formulaParameterActionsContainer',
                  stylex.props(styles.parameterActionsCell).className,
                )}>
                {!readOnly && (
                  <Icon
                    icon={faPlus}
                    testId={`insertParam_${parameter.identifier}`}
                    xstyle={actionStyle}
                    onClick={() => insertParameter?.(`$${parameter.identifier}`)}
                    tooltip={t('ADD')}
                  />
                )}
                {!suppressAddOrRemoveOrChangeParameter && !renderItemCell && (
                  <Icon
                    icon={faPencil}
                    testId={`editParam_${parameter.identifier}`}
                    xstyle={actionStyle}
                    onClick={() => setFormulaParameterModalItem(parameter)}
                    tooltip={t('EDIT')}
                  />
                )}
                {(!suppressAddOrRemoveOrChangeParameter ||
                  (listParametersEditable && parameter.listParameters !== undefined)) && (
                  <Icon
                    icon={faRemove}
                    testId={`removeParam_${parameter.identifier}`}
                    xstyle={actionStyle}
                    onClick={() => removeParameterCallback(parameter.identifier as string)}
                    tooltip={t('REMOVE')}
                  />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {formulaParameterModalItem !== undefined ? (
        <AddFormulaParameterModal
          parameters={parameters}
          parameter={formulaParameterModalItem}
          onSave={updateParameter}
          onClose={() => setFormulaParameterModalItem(undefined)}
          testId="functionParamModal"
        />
      ) : null}
    </>
  );
};
export default FormulaParametersTable;
